import { type AxiosInstance } from 'axios';

import type {
  GameSessionResult,
  GameSessionReviewStatus,
  GameSessionStatus,
} from '../assets/enums/game-session.enum.ts';
import { prepareOrderingForBackend } from '../assets/helpers/utils/prepare-ordering-for-backend.ts';
import { type IGolfHoleGeometry } from './courses-api.ts';
import type {
  CommonListArgs,
  Country,
  GetEntityByIdParams,
  IsoDate,
  Nullable,
  PaginatedList,
  SortDirection,
  UUID,
  Weather,
} from './types.ts';

export class GameSessionsApi {
  constructor(private instance: AxiosInstance) {}

  /** Loads sessions using the documented filters and backend ordering. */
  public async getGameSessions(params: GameSessionsListArgs) {
    return this.instance
      .get<PaginatedList<GameSessionListItem>>('/game-sessions/', {
        params: {
          page: params.page,
          page_size: params.page_size,
          date_from: params.date_from,
          date_to: params.date_to,
          tee_box_device_id: params.tee_box_device_id || undefined,
          hole_box_device_id: params.hole_box_device_id || undefined,
          game_type: params.game_type,
          ordering:
            params.ordering ?? prepareOrderingForBackend(params.sortBy, params.sortDirection),
          partner_id: params.partner_id,
          player_id: params.player_id,
          result: params.result,
          review_status: params.review_status,
          search: params.search,
          show_last_approved: params.show_last_approved,
        },
      })
      .then((res) => res.data);
  }

  /** Loads session details, including adjacent game IDs supplied by the backend. */
  public async getGameSessionById(params: GetEntityByIdParams) {
    return this.instance
      .get<GameSessionDetail>(`/game-sessions/${params.id}/`)
      .then((res) => res.data);
  }

  /** Loads archived camera recordings, without starting or changing live streams. */
  public async getGameSessionRecordings(params: GetEntityByIdParams) {
    return this.instance
      .get<GameSessionRecordingsResponse>(`/game-sessions/${params.id}/recordings/`)
      .then((res) => res.data);
  }

  /** Saves human approval independently of the ML decision and payout processing. */
  public async reviewGameSession({ id, ...body }: GameSessionReviewParams) {
    return this.instance
      .post<GameSessionReviewResponse>(`/game-sessions/${id}/review/`, body)
      .then((res) => res.data);
  }
}

export type GameSessionOrdering =
  | 'created_at'
  | '-created_at'
  | 'completed_at'
  | '-completed_at'
  | 'wager_amount'
  | '-wager_amount'
  | 'confidence_score'
  | '-confidence_score'
  | 'result'
  | '-result';

export type GameSessionSortBy =
  | 'created_at'
  | 'completed_at'
  | 'wager_amount'
  | 'confidence_score'
  | 'result';

export type GameSessionsListArgs = CommonListArgs & {
  page_size?: number;
  date_from?: IsoDate;
  date_to?: IsoDate;
  /** Device UUIDs from the devices API, rather than serial numbers. */
  tee_box_device_id?: UUID;
  hole_box_device_id?: UUID;
  game_type?: string;
  ordering?: GameSessionOrdering;
  sortBy?: GameSessionSortBy;
  sortDirection?: SortDirection;
  partner_id?: string;
  player_id?: string;
  result?: GameSessionResult;
  review_status?: GameSessionReviewStatus;
  search?: string;
  show_last_approved?: boolean;
};

export type GameSessionPlayer = {
  id: string;
  username: Nullable<string>;
  photo: Nullable<string>;
  phone: Nullable<string>;
  games_played: number;
};

export type GameSessionPartner = {
  id: string;
  legal_name: string;
  logo: Nullable<string>;
  business_address: string;
};

export type GameSessionCoursePartner = {
  id: string;
  legal_name: string;
  business_address: string;
};

export type GameSessionCourse = {
  id: string;
  name: string;
  address: string;
  photo: Nullable<string>;
  partner: GameSessionCoursePartner;
  country: Nullable<Country>;
};

export type GameSessionHole = {
  id: string;
  hole_number: number;
  men_par: Nullable<number>;
  wmn_par: Nullable<number>;
};

export type GameSessionHoleDetail = GameSessionHole & {
  geometry: Nullable<IGolfHoleGeometry>;
};

export type GameSessionChallengeConfig = {
  id: number;
  name: string;
  display_name: string;
  game_type: string;
  mode: string;
  units?: 'meters' | 'yards';
  base_entry_fee: string;
  base_payout_odds: string;
  max_shots: number;
  config: unknown;
  description: Nullable<string>;
  created_at: IsoDate;
  updated_at: IsoDate;
  deleted_at?: Nullable<IsoDate>;
};

export type GameSessionShotSideSpinDirection = 'LEFT' | 'RIGHT';

export type GameSessionShotLandingPosition = {
  x?: number;
  y?: number;
  distance_to_pin?: number;
};

export type GameSessionShotSpin = {
  total_spin: number;
  spin_axis: number;
  back_spin: number;
  side_spin: number;
  side_spin_direction: GameSessionShotSideSpinDirection;
};

export type GameSessionShotTrajectoryLaunch = {
  ball_speed: Nullable<number>;
  launch_angle: Nullable<number>;
  launch_direction: Nullable<number>;
  back_spin: Nullable<number>;
  side_spin: Nullable<number>;
  side_spin_direction: Nullable<GameSessionShotSideSpinDirection>;
};

export type GameSessionShotWind = {
  speed_mps: number;
  direction_deg: number;
  cross_mps: number;
  head_mps: number;
};

export type GameSessionShotTrajectory = {
  launch: GameSessionShotTrajectoryLaunch;
  apex: Nullable<number>;
  carry: Nullable<number>;
  landing: Nullable<GameSessionShotLandingPosition>;
  points: Array<[number, number, number]>;
  landing_index: number;
  flight_time: Nullable<number>;
  roll_time: Nullable<number>;
  bounces: number;
  runout: Nullable<number>;
  total_distance: Nullable<number>;
  wind: Nullable<GameSessionShotWind>;
};

export type GameSessionShotOffset = {
  recording_id: string;
  device_id: UUID;
  device_type?: Nullable<'TEE_BOX' | 'HOLE_BOX'>;
  camera?: Nullable<string>;
  offset_sec: number;
  primary: boolean;
};

export type GameSessionShot = {
  id: string;
  game_session_id: string;
  shot_number: number;
  source: 'auto' | 'manual';
  offsets?: GameSessionShotOffset[];
  distance?: Nullable<string>;
  landed_on_fairway?: Nullable<boolean>;
  landing_position?: Nullable<GameSessionShotLandingPosition>;
  video_url?: Nullable<string>;
  is_winning_shot: boolean;
  win_type?: Nullable<string>;
  calculated_payout?: Nullable<string>;
  garmin_shot_id?: Nullable<number>;
  shot_type?: Nullable<string>;
  recorded_at?: Nullable<IsoDate>;
  shot_time_source?: Nullable<'device' | 'detection' | 'server'>;
  record_ref?: Nullable<string>;
  /** All launch speeds are supplied in metres per second. */
  ball_speed?: Nullable<string>;
  club_head_speed?: Nullable<string>;
  launch_angle?: Nullable<string>;
  launch_direction?: Nullable<string>;
  total_spin?: Nullable<string>;
  spin_axis?: Nullable<string>;
  carry?: Nullable<string>;
  apex?: Nullable<string>;
  confidence_score?: Nullable<string>;
  wind_speed_mps?: Nullable<string>;
  wind_direction_deg?: Nullable<string>;
  garmin_payload?: Nullable<Record<string, unknown>>;
  created_at: IsoDate;
  extra?: Nullable<GameSessionShotExtra>;
  spin?: Nullable<GameSessionShotSpin>;
  trajectory?: Nullable<GameSessionShotTrajectory>;
};

export type GameSessionShotExtra = {
  trajectory?: Array<{ x: number; y: number; z: number }>;
  world_trajectory?: Nullable<GameSessionShotWorldTrajectory>;
};

export type GameSessionShotWorldTrajectory = {
  version: number;
  coordinate_system: string;
  height_reference: string;
  origin: { latitude: number; longitude: number };
  points: Array<{ latitude: number; longitude: number; height_m: number }>;
};

export type GameSessionListItem = {
  id: string;
  player: Nullable<GameSessionPlayer>;
  player_name: Nullable<string>;
  player_phone: string;
  partner: Nullable<GameSessionPartner>;
  course: Nullable<GameSessionCourse>;
  // TODO: The backend currently always returns null; restore hole data for session geometry.
  hole: Nullable<GameSessionHole>;
  challenge_config_id: number;
  challenge_config: Nullable<GameSessionChallengeConfig>;
  status: GameSessionStatus;
  wager_amount: string;
  potential_payout: Nullable<string>;
  final_payout: string;
  created_at: IsoDate;
  started_at: Nullable<IsoDate>;
  completed_at: Nullable<IsoDate>;
  payment_intent_id: Nullable<string>;
  shots?: GameSessionShot[];
  device_snapshot: unknown;
  weather: Nullable<Weather>;
  ml_approval: GameSessionMlApproval;
  human_approval: GameSessionHumanApproval;
  review_status: GameSessionReviewStatus;
  result: Nullable<GameSessionResult>;
  reviewed_by: Nullable<string>;
  reviewed_at: Nullable<IsoDate>;
  moderator_comment: Nullable<string>;
  is_last_approved: boolean;
  confidence_score: Nullable<string>;
  tee_box_device_id: Nullable<UUID>;
  hole_box_device_id: Nullable<UUID>;
  latitude?: Nullable<number>;
  longitude?: Nullable<number>;
  flight_s3_url?: Nullable<string>;
  flight_meta?: Nullable<GameSessionFlightMeta>;
  shot_media: Nullable<GameSessionShotMedia[]>;
  updated_at: IsoDate;
  deleted_at: Nullable<IsoDate>;
};

/** Flight metadata has a documented device UUID and permits additional backend fields. */
export type GameSessionFlightMeta = {
  device_id?: UUID;
  [key: string]: unknown;
};

export type GameSessionDetail = Omit<GameSessionListItem, 'hole'> & {
  // TODO: The backend currently always returns null; restore hole geometry in session details.
  hole: Nullable<GameSessionHoleDetail>;
  navigation: GameSessionNavigation;
};

export type GameSessionNavigation = {
  previous_game_id: Nullable<string>;
  next_game_id: Nullable<string>;
};

export type GameSessionMlApproval = {
  result: Nullable<GameSessionResult>;
  confidence_score: Nullable<number>;
  reason: Nullable<string>;
  decided_at: Nullable<IsoDate>;
};

export type GameSessionHumanApproval = {
  review_status: GameSessionReviewStatus;
  result: Nullable<GameSessionResult>;
  moderator_comment: Nullable<string>;
  reviewed_by: Nullable<string>;
  reviewed_at: Nullable<IsoDate>;
};

export type GameSessionShotMedia = {
  shot_number: number;
  thumbnails: string[];
  streams: GameSessionStream[];
};

export type GameSessionStream = {
  url: string;
  protocol: 'hls' | 'mp4' | 'webrtc';
};

export type GameSessionRecording = {
  id: string;
  device_id?: Nullable<UUID>;
  device_type?: Nullable<'TEE_BOX' | 'HOLE_BOX'>;
  camera_id?: Nullable<string>;
  camera_label?: Nullable<string>;
  status: 'pending' | 'recording' | 'stopping' | 'completed' | 'failed';
  video_type?: Nullable<GameSessionRecordingVideoType>;
  video_meta?: Nullable<GameSessionRecordingVideoMeta>;
  error?: Nullable<string>;
  started_at?: Nullable<IsoDate>;
  ended_at?: Nullable<IsoDate>;
  s3_url?: Nullable<string>;
};

export type GameSessionRecordingVideoType = 'raw' | 'dewarp';

export type GameSessionRecordingVideoMeta = {
  source: {
    camera_name?: Nullable<string>;
    width?: Nullable<number>;
    height?: Nullable<number>;
    fps?: Nullable<number>;
    bitrate_kbps?: Nullable<number>;
  };
  container: string;
  encoding: 'copy' | 'libx264';
  dewarp_params?: Nullable<Record<string, unknown>>;
  from_ring?: boolean;
  file_size_bytes?: Nullable<number>;
  duration_sec?: Nullable<number>;
};

export type GameSessionRecordingsResponse = {
  session_id: string;
  recordings: GameSessionRecording[];
};

export type GameSessionReviewParams = {
  id: string;
  result: GameSessionResult;
  moderator_comment?: string;
};

export type GameSessionReviewResponse = Pick<
  GameSessionListItem,
  'id' | 'human_approval' | 'ml_approval'
>;
